import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FaSearch,
  FaUsers,
  FaWhatsapp,
  FaEnvelope,
  FaFilter,
  FaTimes,
} from "react-icons/fa";
import {
  openModal,
  closeModal,
  setSegments,
  setContacts,
  setLoading,
  setError,
  isModalOpen,
} from "../../../../state/reducers/communicationSlice";
import { getSegments } from "../../../../services/communication.services";
import ManageSegments from "./ManageSegments";
import CreateContact from "./CreateContact";

const Contacts = () => {
  const dispatch = useDispatch();
  const { isModalOpen, segments, contacts, isLoading, error } = useSelector(
    (state) => state.communication
  );

  const [activeTab, setActiveTab] = useState("WhatsApp");
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);
  const [showManageSegments, setShowManageSegments] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const contactsPerPage = 10;

  // Check if screen is mobile
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    if (!segments || segments.length === 0) {
      fetchSegments();
    }
  }, []);

  // Fetch segments on component mount
  const fetchSegments = async () => {
    dispatch(setLoading(true));
    try {
      const response = await getSegments();
      if (response && response.segment_counts) {
        dispatch(setSegments(response.segment_counts));
      } else {
        dispatch(setError("Unexpected segment response structure."));
      }
    } catch (err) {
      dispatch(setError(err.message || "Failed to fetch segments."));
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Fetch contacts on component mount
  const fetchContacts = async () => {
    dispatch(setLoading(true));
    try {
      const response = await getSegments(); // Replace with getContacts() if available
      if (response && response.contacts) {
        dispatch(setContacts(response.contacts));
      } else {
        dispatch(setError("Unexpected contacts response structure."));
      }
    } catch (err) {
      dispatch(setError(err.message || "Failed to fetch contacts."));
    } finally {
      dispatch(setLoading(false));
    }
  };
  useEffect(() => {
    if (!contacts || contacts.length === 0) {
      fetchContacts();
    }
  }, []);

  const openModalHandler = () => dispatch(openModal());
  const closeModalHandler = () => dispatch(closeModal());

  if (showManageSegments) {
    return (
      <ManageSegments
        fetchContacts={fetchContacts}
        segments={segments}
        onBack={() => setShowManageSegments(false)}
      />
    );
  }

  // Function to get the segment name by segment_id
  const getSegmentNameById = (segmentId) => {
    const segment = segments.find((seg) => seg.segment__id === segmentId);
    return segment ? segment.segment__name : "Unknown";
  };

  // Filter segments based on the active tab (WhatsApp or Email)
  const filteredSegments = Array.isArray(segments)
    ? segments?.filter(
        (segment) =>
          (segment?.segment__category__name || "")
            .toString()
            .trim()
            .toLowerCase() === activeTab.toLowerCase()
      )
    : [];

  // Filter contacts based on search input
  const filteredContacts = Array.isArray(contacts)
    ? contacts
        .filter(
          (contact) =>
            getSegmentNameById(contact.segment) ===
            getSegmentNameById(selectedSegment)
        )

        .filter((contact) => {
          const query = searchQuery.toLowerCase();
          return (
            contact.name?.toLowerCase().includes(query) ||
            contact.email?.toLowerCase().includes(query) ||
            contact.phone_number?.toLowerCase().includes(query) ||
            getSegmentNameById(contact.segment)?.toLowerCase().includes(query)
          );
        })
    : [];

  const indexOfLastContact = currentPage * contactsPerPage;
  const indexOfFirstContact = indexOfLastContact - contactsPerPage;
  const currentContacts = filteredContacts.slice(
    indexOfFirstContact,
    indexOfLastContact
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Mobile Contact Card Component
  const ContactCard = ({ contact, index }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-semibold text-gray-900">{contact.name}</h3>
          <p className="text-sm text-gray-500">
            #{indexOfFirstContact + index + 1}
          </p>
        </div>
        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
          {getSegmentNameById(contact.segment)}
        </span>
      </div>

      <div className="space-y-2">
        <div className="flex items-center text-sm">
          <FaEnvelope className="text-gray-400 mr-2" />
          <span className="text-gray-700">{contact.email}</span>
        </div>

        {contact.phone_number && (
          <div className="flex items-center text-sm">
            <FaWhatsapp className="text-gray-400 mr-2" />
            <span className="text-gray-700">{contact.phone_number}</span>
          </div>
        )}

        <div className="flex justify-between items-center pt-2 border-t border-gray-100">
          <div>
            <span className="text-xs text-gray-500">Email Status:</span>
            <span
              className={`ml-1 text-xs px-2 py-1 rounded-full ${
                contact.email_status === "Active"
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {contact.email_status || "N/A"}
            </span>
          </div>

          <div>
            <span className="text-xs text-gray-500">WhatsApp:</span>
            <span
              className={`ml-1 text-xs px-2 py-1 rounded-full ${
                contact.whatsapp_status === "Active"
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {contact.whatsapp_status || "N/A"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // Segment Card Component
  const SegmentCard = ({ segment, onClick }) => (
    <div
      className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
        {segment.segment__name}
      </h3>
      <div className="flex items-center justify-between">
        <p className="text-2xl sm:text-3xl font-bold text-gray-900">
          {segment.total_contacts || 0}
        </p>
        <FaUsers className="text-gray-400 text-xl" />
      </div>
      <p className="text-sm text-gray-500 mt-1">contacts</p>
    </div>
  );

  // Pagination Component
  const Pagination = () => {
    const totalPages = Math.ceil(filteredContacts.length / contactsPerPage);
    if (totalPages <= 1) return null;

    return (
      <div className="flex justify-center items-center mt-6 space-x-2">
        <button
          onClick={() => paginate(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Previous
        </button>

        {[...Array(Math.min(5, totalPages))].map((_, i) => {
          const pageNum = i + 1;
          return (
            <button
              key={pageNum}
              onClick={() => paginate(pageNum)}
              className={`px-3 py-2 border rounded-md ${
                currentPage === pageNum
                  ? "bg-purple-600 text-white border-purple-600"
                  : "border-gray-300 hover:bg-gray-50"
              }`}
            >
              {pageNum}
            </button>
          );
        })}

        <button
          onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
        >
          Next
        </button>
      </div>
    );
  };
  const handleSelect = (id) => {
    setSelectedSegment(id);
  };

  return (
    <div
      className={`min-h-screen bg-gray-50 transition-all duration-300 ${
        isSideBarOpen ? "ml-0 lg:ml-80" : "ml-0 md:ml-20"
      }`}
    >
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
          <div>
            <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800">
              Contacts
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              This is your contact database. From here, you can view, organize,
              and manage your contacts.
            </p>
          </div>
          <button
            className="w-full sm:w-auto bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:ring-opacity-50 transition-colors"
            onClick={openModalHandler}
          >
            + Import Contacts
          </button>
        </div>
        {isModalOpen && (
          <CreateContact
            fetchContacts={fetchContacts}
            onClose={closeModalHandler}
          />
        )}
      </div>

      {/* Tabs Section */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex justify-center space-x-1">
          <button
            onClick={() => setActiveTab("WhatsApp")}
            className={`flex-1 sm:flex-none sm:px-8 py-3 rounded-lg transition-colors ${
              activeTab === "WhatsApp"
                ? "bg-purple-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <FaWhatsapp className="inline mr-2" />
            WhatsApp
          </button>
          <button
            onClick={() => setActiveTab("Email")}
            className={`flex-1 sm:flex-none sm:px-8 py-3 rounded-lg transition-colors ${
              activeTab === "Email"
                ? "bg-purple-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <FaEnvelope className="inline mr-2" />
            Email
          </button>
        </div>
      </div>

      {/* Segments Section */}
      <div className="p-4 sm:p-6 lg:p-8 bg-white border-b border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-3 sm:space-y-0">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">
            Segments
          </h2>
          <button
            onClick={() => setShowManageSegments(true)}
            className="text-purple-600 font-medium hover:text-purple-700 transition-colors"
          >
            Manage Segments
          </button>
        </div>

        {/* Segments Grid */}
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <span className="ml-3 text-gray-600">Loading segments...</span>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-600">{error}</p>
          </div>
        ) : filteredSegments.length === 0 ? (
          <div className="text-center py-8">
            <FaUsers className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <p className="text-gray-500">
              No segments available for {activeTab}.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {filteredSegments.map((segment, index) => (
              <SegmentCard
                key={index}
                segment={segment}
                onClick={() => handleSelect(segment.segment__id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Contacts List Section */}
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">
            All Contact List
          </h2>

          {/* Search Bar */}
          <div className="relative w-full sm:w-1/3 lg:w-1/4">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full py-2 px-4 pl-10 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-purple-600"
            />
          </div>
        </div>

        {/* Selected Segment Filter */}
        {selectedSegment && (
          <div className="mb-4 flex items-center space-x-2">
            <span className="text-sm text-gray-600">Filtered by:</span>
            <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">
              {getSegmentNameById(selectedSegment)}
            </span>
            <button
              onClick={() => setSelectedSegment(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <FaTimes />
            </button>
          </div>
        )}

        {/* Contacts Display */}
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <span className="ml-3 text-gray-600">Loading contacts...</span>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-600">{error}</p>
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="text-center py-8">
            <FaUsers className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <p className="text-gray-500">No contacts found.</p>
            {selectedSegment && (
              <button
                onClick={() => setSelectedSegment(null)}
                className="mt-2 text-purple-600 hover:text-purple-700"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Mobile View - Cards */}
            <div className="block md:hidden">
              {currentContacts.map((contact, index) => (
                <ContactCard key={index} contact={contact} index={index} />
              ))}
            </div>

            {/* Desktop View - Table */}
            <div className="hidden md:block overflow-x-auto bg-white rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      SR No.
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Segment
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      WhatsApp Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentContacts.map((contact, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {indexOfFirstContact + index + 1}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                          {getSegmentNameById(contact.segment)}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {contact.name}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {contact.email}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {contact.phone_number || "N/A"}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            contact.email_status === "Active"
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {contact.email_status || "N/A"}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            contact.whatsapp_status === "Active"
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {contact.whatsapp_status || "N/A"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination />
          </>
        )}
      </div>
    </div>
  );
};

export default Contacts;
